import React, { useState, useRef, useEffect } from 'react';
import { Loader2, Search } from 'lucide-react';
import { adminAPI, productAPI } from '../../../services/api';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { CustomerIntelligenceWidget } from './CustomerIntelligenceWidget';
import { EDIT_STATUS_OPTIONS, TRAFFIC_PLATFORM_OPTIONS, printPackingSlipHtml } from './orderModalConstants';

export interface ManualOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOrderCreated: (newOrder: any) => void;
  deliveryOptions: any[];
  siteSettings?: { site_name: string; support_phone: string; logo_url?: string };
}

export const ManualOrderModal: React.FC<ManualOrderModalProps> = ({
  isOpen,
  onClose,
  onOrderCreated,
  deliveryOptions,
  siteSettings,
}) => {
  const isMobile = useIsMobile();
  const [manualModalTab, setManualModalTab] = useState<'items' | 'slip' | 'fraud' | 'form'>('items');
  const [manualSaving, setManualSaving] = useState(false);
  const [manualError, setManualError] = useState<string>('');
  const [manualSuccess, setManualSuccess] = useState<string>('');
  const [manualProductSearch, setManualProductSearch] = useState<string>('');
  const [manualProductResults, setManualProductResults] = useState<any[]>([]);
  const [manualProductSearching, setManualProductSearching] = useState(false);
  const [manualCartItems, setManualCartItems] = useState<any[]>([]);
  const [manualPickingProduct, setManualPickingProduct] = useState<any>(null);
  const [manualPickAttrs, setManualPickAttrs] = useState<{ color: string; size: string; weight: string; qty: number; price: number }>({ color: '', size: '', weight: '', qty: 1, price: 0 });
  const [manualForm, setManualForm] = useState({
    customer_name: '',
    customer_phone: '',
    shipping_address: '',
    notes: '',
    payment_method: 'cash_on_delivery',
    delivery_option: null as number | null,
    shipping_cost: '60',
    initial_status: 'pending',
    admin_discount_type: null as 'flat' | 'percentage' | null,
    admin_discount_value: '',
    coupon_code: '',
    traffic_source: 'manual',
    utm_campaign: '',
    send_email: false,
  });
  const [manualCouponApplied, setManualCouponApplied] = useState<{ code: string; discount: number } | null>(null);
  const [manualCouponChecking, setManualCouponChecking] = useState(false);
  const [manualCouponError, setManualCouponError] = useState<string>('');
  const manualSearchRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const manualPackingSlipRef = useRef<HTMLDivElement>(null);

  // Customer Steadfast History
  const [customerHistory, setCustomerHistory] = useState<any>(null);
  const [customerHistoryLoading, setCustomerHistoryLoading] = useState(false);
  const [customerHistoryError, setCustomerHistoryError] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      const defaultOpt = deliveryOptions[0];
      setManualForm({
        customer_name: '',
        customer_phone: '',
        shipping_address: '',
        notes: '',
        payment_method: 'cash_on_delivery',
        delivery_option: defaultOpt ? defaultOpt.id : null,
        shipping_cost: defaultOpt ? String(parseFloat(defaultOpt.cost).toFixed(0)) : '60',
        initial_status: 'pending',
        admin_discount_type: null,
        admin_discount_value: '',
        coupon_code: '',
        traffic_source: 'manual',
        utm_campaign: '',
        send_email: false,
      });
      setManualCartItems([]);
      setManualPickingProduct(null);
      setManualPickAttrs({ color: '', size: '', weight: '', qty: 1, price: 0 });
      setManualProductSearch('');
      setManualProductResults([]);
      setManualCouponApplied(null);
      setManualCouponError('');
      setManualError('');
      setManualSuccess('');
      setManualModalTab('items');
      setCustomerHistory(null);
    }
  }, [isOpen, deliveryOptions]);

  const loadCustomerHistory = async (phone: string) => {
    if (!phone) {
      setCustomerHistory(null);
      return;
    }
    setCustomerHistoryLoading(true);
    setCustomerHistoryError('');
    try {
      const res = await adminAPI.getCustomerSteadfastHistory(phone);
      setCustomerHistory(res.data || null);
    } catch (err: any) {
      const msg = err?.response?.data?.error || '';
      if (msg && !msg.includes('not configured')) {
        setCustomerHistoryError(msg);
      }
    } finally {
      setCustomerHistoryLoading(false);
    }
  };

  const handleManualPhoneChange = (val: string) => {
    setManualForm(prev => ({ ...prev, customer_phone: val }));
    const clean = val.replace(/[^0-9]/g, '');
    if (clean.length === 11) {
      loadCustomerHistory(clean);
    }
  };

  const handleManualDeliveryOptionChange = (optId: number | null) => {
    const opt = deliveryOptions.find(o => o.id === optId);
    setManualForm(prev => ({
      ...prev,
      delivery_option: optId,
      shipping_cost: opt ? String(parseFloat(opt.cost).toFixed(0)) : prev.shipping_cost,
    }));
  };

  const handleManualProductSearch = (query: string) => {
    setManualProductSearch(query);
    if (manualSearchRef.current) clearTimeout(manualSearchRef.current);
    if (!query.trim()) { setManualProductResults([]); return; }
    manualSearchRef.current = setTimeout(async () => {
      setManualProductSearching(true);
      try {
        const res = await productAPI.getProducts({ search: query, page: 1, limit: 10 } as any);
        const results = (res as any).results || res;
        setManualProductResults(Array.isArray(results) ? results : []);
      } catch { setManualProductResults([]); }
      finally { setManualProductSearching(false); }
    }, 300);
  };

  const handleSelectManualProduct = (prod: any) => {
    setManualPickingProduct(prod);
    const p = parseFloat(prod.discount_price || prod.price) || 0;
    setManualPickAttrs({ color: '', size: '', weight: '', qty: 1, price: p });
    setManualProductResults([]);
    setManualProductSearch('');
  };

  const handleManualAddToCart = () => {
    if (!manualPickingProduct) return;
    const unitPrice = manualPickAttrs.price || parseFloat(manualPickingProduct.discount_price || manualPickingProduct.price) || 0;
    const existing = manualCartItems.findIndex(
      it => it.product_id === manualPickingProduct.id &&
            it.selected_color === manualPickAttrs.color &&
            it.selected_size === manualPickAttrs.size &&
            it.selected_weight === manualPickAttrs.weight
    );
    if (existing >= 0) {
      const updated = [...manualCartItems];
      updated[existing].quantity = (updated[existing].quantity || 1) + manualPickAttrs.qty;
      setManualCartItems(updated);
    } else {
      setManualCartItems(prev => [...prev, {
        product_id: manualPickingProduct.id,
        product_name: manualPickingProduct.name_en,
        product_image: manualPickingProduct.image || manualPickingProduct.images?.[0]?.image || '',
        product_sku: manualPickingProduct.sku || '',
        product_stock: manualPickingProduct.stock !== undefined ? manualPickingProduct.stock : 99,
        product_attributes: manualPickingProduct.attributes || {},
        quantity: manualPickAttrs.qty,
        price: unitPrice,
        selected_color: manualPickAttrs.color,
        selected_size: manualPickAttrs.size,
        selected_weight: manualPickAttrs.weight,
        selected_attributes: {},
      }]);
    }
    setManualPickingProduct(null);
    setManualPickAttrs({ color: '', size: '', weight: '', qty: 1, price: 0 });
    setManualProductSearch('');
    setManualProductResults([]);
  };

  const handleUpdateManualItemQty = (index: number, newQty: number) => {
    setManualCartItems(prev => {
      const copy = [...prev];
      if (copy[index]) {
        copy[index] = { ...copy[index], quantity: Math.max(1, newQty) };
      }
      return copy;
    });
  };

  const handleUpdateManualItemPrice = (index: number, newPrice: number) => {
    setManualCartItems(prev => {
      const copy = [...prev];
      if (copy[index]) {
        copy[index] = { ...copy[index], price: Math.max(0, newPrice) };
      }
      return copy;
    });
  };

  const handleUpdateManualItemVariant = (index: number, field: 'selected_color' | 'selected_size' | 'selected_weight', value: string) => {
    setManualCartItems(prev => {
      const copy = [...prev];
      if (copy[index]) {
        copy[index] = { ...copy[index], [field]: value };
      }
      return copy;
    });
  };

  const handleRemoveManualItem = (index: number) => {
    setManualCartItems(prev => prev.filter((_, i) => i !== index));
  };

  const manualItemsSubtotal = manualCartItems.reduce(
    (sum, it) => sum + (parseFloat(it.price || 0) * parseInt(it.quantity || it.qty || 1)),
    0
  );
  const manualShipping = parseFloat(manualForm.shipping_cost) || 0;
  const manualCouponDiscount = manualCouponApplied?.discount || 0;
  let manualAdminDiscount = 0;
  if (manualForm.admin_discount_type === 'flat' && manualForm.admin_discount_value) {
    manualAdminDiscount = Math.min(parseFloat(manualForm.admin_discount_value) || 0, manualItemsSubtotal);
  } else if (manualForm.admin_discount_type === 'percentage' && manualForm.admin_discount_value) {
    manualAdminDiscount = (manualItemsSubtotal * (parseFloat(manualForm.admin_discount_value) || 0)) / 100;
  }
  const manualTotal = Math.max(0, manualItemsSubtotal + manualShipping - manualCouponDiscount - manualAdminDiscount);

  const handleManualApplyCoupon = async () => {
    const code = manualForm.coupon_code.trim();
    if (!code) return;
    setManualCouponChecking(true);
    setManualCouponError('');
    setManualCouponApplied(null);
    try {
      const coupons: any = await adminAPI.getCoupons();
      const list = coupons.data?.results || coupons.data || [];
      const found = list.find((c: any) => c.code === code && c.is_active);
      if (found) {
        let disc = 0;
        if (found.discount_type === 'percentage') {
          disc = (manualItemsSubtotal * found.discount_value) / 100;
          if (found.max_discount) disc = Math.min(disc, found.max_discount);
        } else {
          disc = parseFloat(found.discount_value);
        }
        setManualCouponApplied({ code, discount: disc });
      } else {
        setManualCouponError('Invalid or inactive coupon');
      }
    } catch { setManualCouponError('Could not validate coupon'); }
    finally { setManualCouponChecking(false); }
  };

  const handlePrintSlip = () => {
    if (manualPackingSlipRef.current) {
      printPackingSlipHtml(manualPackingSlipRef.current, 'Manual Order');
    }
  };

  const handleManualCreateOrder = async (andPrint = false) => {
    setManualError('');
    if (!manualForm.customer_name.trim()) { setManualError('Customer name is required'); return; }
    if (!manualForm.customer_phone.trim()) { setManualError('Phone number is required'); return; }
    if (!manualForm.shipping_address.trim()) { setManualError('Shipping address is required'); return; }
    if (manualCartItems.length === 0) { setManualError('Add at least one product to the order'); return; }

    setManualSaving(true);
    try {
      const payload: any = {
        customer_name: manualForm.customer_name.trim(),
        customer_phone: manualForm.customer_phone.trim(),
        shipping_address: manualForm.shipping_address.trim(),
        notes: manualForm.notes.trim(),
        payment_method: manualForm.payment_method,
        delivery_option: manualForm.delivery_option || null,
        shipping_cost: parseFloat(manualForm.shipping_cost) || 60,
        initial_status: manualForm.initial_status,
        send_email: manualForm.send_email,
        admin_discount_type: manualForm.admin_discount_type || null,
        admin_discount_value: manualForm.admin_discount_value ? parseFloat(manualForm.admin_discount_value) : null,
        traffic_source: manualForm.traffic_source || 'manual',
        utm_campaign: manualForm.utm_campaign.trim(),
        items: manualCartItems.map(it => ({
          product_id: it.product_id,
          quantity: it.quantity || it.qty || 1,
          selected_color: it.selected_color || '',
          selected_size: it.selected_size || '',
          selected_weight: it.selected_weight || '',
          selected_attributes: it.selected_attributes || {},
          unit_price: parseFloat(it.price) || null,
        })),
      };
      if (manualCouponApplied) payload.coupon_code = manualCouponApplied.code;

      const res: any = await adminAPI.createManualOrder(payload);
      const newOrder = res.data;
      onOrderCreated(newOrder);
      setManualSuccess(`Order #${newOrder.order_number} created successfully!`);

      if (andPrint) {
        setTimeout(() => {
          handlePrintSlip();
        }, 300);
      }

      setTimeout(() => {
        onClose();
        setManualSuccess('');
        setManualCartItems([]);
      }, andPrint ? 2500 : 1200);
    } catch (err: any) {
      const data = err?.response?.data;
      if (typeof data === 'object') {
        const msgs = Object.entries(data).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' | ');
        setManualError(msgs);
      } else {
        setManualError('Failed to create order. Please check inputs.');
      }
    } finally {
      setManualSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(15, 23, 42, 0.72)',
          backdropFilter: 'blur(5px)',
          zIndex: 9000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: isMobile ? '8px' : '18px'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: '14px',
            width: isMobile ? '100%' : '95vw',
            maxWidth: '1240px',
            height: isMobile ? '96vh' : '92vh',
            maxHeight: '94vh',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.35)',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #1e293b, #0f172a)',
              padding: '12px 20px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              flexShrink: 0
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '34px', height: '34px', borderRadius: '8px',
                  background: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.4)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#60a5fa', fontSize: '1.1rem'
                }}>
                  ➕
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <h2 style={{ color: '#f8fafc', margin: 0, fontSize: '1.05rem', fontWeight: '700', letterSpacing: '-0.02em' }}>
                      Create Manual Order
                    </h2>
                    <span style={{ fontSize: '0.72rem', background: '#3b82f6', color: '#fff', padding: '2px 8px', borderRadius: '12px', fontWeight: '700' }}>
                      Phone / Call-in
                    </span>
                  </div>
                  <p style={{ color: '#94a3b8', margin: '2px 0 0', fontSize: '0.72rem' }}>
                    Direct admin order entry with live inventory, custom pricing, courier packing slip &amp; fraud check
                  </p>
                </div>
              </div>

              {/* Right Side Top Header Section: Actions & Close */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                {/* ✓ Create Order Button */}
                <button
                  type="button"
                  onClick={() => handleManualCreateOrder(false)}
                  disabled={manualSaving || manualCartItems.length === 0}
                  style={{
                    padding: isMobile ? '6px 12px' : '8px 18px',
                    borderRadius: '8px',
                    background: (manualSaving || manualCartItems.length === 0)
                      ? '#475569'
                      : 'linear-gradient(135deg, #4f46e5, #4338ca)',
                    color: (manualSaving || manualCartItems.length === 0) ? '#94a3b8' : '#fff',
                    border: 'none',
                    fontSize: isMobile ? '0.78rem' : '0.86rem',
                    fontWeight: '700',
                    cursor: (manualSaving || manualCartItems.length === 0) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: (manualSaving || manualCartItems.length === 0) ? 'none' : '0 3px 12px rgba(79, 70, 229, 0.4)',
                    transition: 'all 0.15s',
                    whiteSpace: 'nowrap'
                  }}
                  title="Create and save manual order"
                >
                  {manualSaving ? 'Creating...' : '✓ Create Order'}
                </button>

                {/* 🖨️ Create & Print Packing Slip Button */}
                <button
                  type="button"
                  onClick={() => handleManualCreateOrder(true)}
                  disabled={manualSaving || manualCartItems.length === 0}
                  style={{
                    padding: isMobile ? '6px 10px' : '8px 14px',
                    borderRadius: '8px',
                    background: (manualSaving || manualCartItems.length === 0) ? '#1e293b' : '#0f172a',
                    color: (manualSaving || manualCartItems.length === 0) ? '#64748b' : '#f8fafc',
                    border: '1px solid rgba(255,255,255,0.2)',
                    fontSize: isMobile ? '0.74rem' : '0.82rem',
                    fontWeight: '700',
                    cursor: (manualSaving || manualCartItems.length === 0) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                    transition: 'all 0.15s',
                    whiteSpace: 'nowrap'
                  }}
                  title="Create order and open printable Steadfast packing slip"
                >
                  🖨️ {isMobile ? 'Print Slip' : 'Create & Print Packing Slip'}
                </button>

                <button
                  type="button"
                  onClick={() => onClose()}
                  style={{
                    background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)',
                    color: '#fff', borderRadius: '7px', width: '30px', height: '30px',
                    fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', transition: 'all 0.15s',
                    marginLeft: '2px', flexShrink: 0
                  }}
                  title="Close modal"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Mobile Tab Bar */}
            {isMobile && (
              <div style={{
                display: 'flex', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', flexShrink: 0
              }}>
                {([
                  { key: 'form', label: '📋 Order Form' },
                  { key: 'items', label: `🛒 Items (${manualCartItems.length})` },
                  { key: 'slip', label: '🧾 Slip' },
                  { key: 'fraud', label: '🛡️ Fraud' },
                ] as { key: 'items' | 'form' | 'slip' | 'fraud'; label: string }[]).map(tab => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setManualModalTab(tab.key)}
                    style={{
                      flex: 1, padding: '10px 4px', fontSize: '0.78rem', fontWeight: '700',
                      border: 'none', background: manualModalTab === tab.key ? '#fff' : 'transparent',
                      color: manualModalTab === tab.key ? '#2563eb' : '#64748b',
                      borderBottom: manualModalTab === tab.key ? '2px solid #2563eb' : '2px solid transparent',
                      cursor: 'pointer'
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {/* Body */}
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
              {/* ── LEFT: Manual Order Form Sidebar ── */}
              {(!isMobile || manualModalTab === 'form') && (
                <div style={{
                  width: isMobile ? '100%' : '360px',
                  minWidth: isMobile ? undefined : '330px',
                  flexShrink: 0,
                  padding: '16px 18px', overflowY: 'auto',
                  borderRight: isMobile ? 'none' : '1px solid #e2e8f0',
                  backgroundColor: '#f8fafc'
                }}>
                  {/* Move to Category / Initial Status Dropdown */}
                  <div style={{ marginBottom: '14px', background: '#fff', padding: '12px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{
                        fontSize: '0.74rem',
                        fontWeight: '800',
                        color: '#475569',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '2.5px solid #3b82f6',
                        paddingBottom: '3px',
                        display: 'inline-block'
                      }}>
                        MOVE TO CATEGORY
                      </span>
                      {(() => {
                        const cur = EDIT_STATUS_OPTIONS.find(st => st.value === manualForm.initial_status) || {
                          label: manualForm.initial_status,
                          color: '#475569',
                          bg: '#f1f5f9',
                          border: '#cbd5e1'
                        };
                        return (
                          <span style={{
                            fontSize: '0.72rem',
                            fontWeight: '700',
                            padding: '3px 10px',
                            borderRadius: '12px',
                            background: cur.bg,
                            color: cur.color,
                            border: `1px solid ${cur.border}`
                          }}>
                            ● {cur.label}
                          </span>
                        );
                      })()}
                    </div>

                    <select
                      value={manualForm.initial_status}
                      onChange={e => setManualForm(p => ({ ...p, initial_status: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '9px 12px',
                        border: '1.5px solid #cbd5e1',
                        borderRadius: '7px',
                        fontSize: '0.88rem',
                        fontWeight: '600',
                        color: '#1e293b',
                        backgroundColor: '#f8fafc',
                        cursor: 'pointer',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    >
                      {EDIT_STATUS_OPTIONS.map(st => (
                        <option key={st.value} value={st.value}>
                          {st.icon} {st.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Order Platform / Source Dropdown */}
                  <div style={{ marginBottom: '14px', background: '#fff', padding: '12px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{
                        fontSize: '0.74rem',
                        fontWeight: '800',
                        color: '#475569',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '2.5px solid #8b5cf6',
                        paddingBottom: '3px',
                        display: 'inline-block'
                      }}>
                        ORDER PLATFORM / SOURCE
                      </span>
                      {(() => {
                        const cur = TRAFFIC_PLATFORM_OPTIONS.find(p => p.value === manualForm.traffic_source) || TRAFFIC_PLATFORM_OPTIONS[0];
                        return (
                          <span style={{
                            fontSize: '0.72rem',
                            fontWeight: '700',
                            padding: '3px 10px',
                            borderRadius: '12px',
                            background: cur.bg,
                            color: cur.color,
                            border: `1px solid ${cur.border}`
                          }}>
                            ● {cur.label}
                          </span>
                        );
                      })()}
                    </div>

                    <select
                      value={manualForm.traffic_source}
                      onChange={e => setManualForm(p => ({ ...p, traffic_source: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '9px 12px',
                        border: '1.5px solid #cbd5e1',
                        borderRadius: '7px',
                        fontSize: '0.88rem',
                        fontWeight: '600',
                        color: '#1e293b',
                        backgroundColor: '#f8fafc',
                        cursor: 'pointer',
                        outline: 'none',
                        boxSizing: 'border-box',
                        marginBottom: '8px'
                      }}
                    >
                      {TRAFFIC_PLATFORM_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.icon} {opt.label}
                        </option>
                      ))}
                    </select>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: '#64748b', marginBottom: '3px' }}>
                        Campaign / Ad Name (Optional)
                      </label>
                      <input
                        type="text"
                        value={manualForm.utm_campaign}
                        onChange={e => setManualForm(p => ({ ...p, utm_campaign: e.target.value }))}
                        placeholder="e.g. Eid Campaign, FB Ad 1, Post 2..."
                        style={{
                          width: '100%',
                          padding: '7px 10px',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          fontSize: '0.82rem',
                          color: '#1e293b',
                          outline: 'none',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>

                  <p style={{ margin: '4px 0 10px', fontSize: '0.72rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '2px solid #3b82f6', paddingBottom: '4px', display: 'inline-block' }}>
                    Customer Details
                  </p>

                  {manualError && (
                    <div style={{
                      background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5',
                      borderRadius: '8px', padding: '9px 12px', marginBottom: '14px', fontSize: '12px'
                    }}>
                      ⚠️ {manualError}
                    </div>
                  )}

                  {manualSuccess && (
                    <div style={{
                      background: '#dcfce7', color: '#15803d', border: '1px solid #86efac',
                      borderRadius: '8px', padding: '9px 12px', marginBottom: '14px', fontSize: '12px', fontWeight: '600'
                    }}>
                      ✅ {manualSuccess}
                    </div>
                  )}

                  {/* Customer Name */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#475569', marginBottom: '4px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      Customer Name *
                    </label>
                    <input
                      type="text"
                      value={manualForm.customer_name}
                      onChange={e => setManualForm(p => ({ ...p, customer_name: e.target.value }))}
                      placeholder="Customer full name"
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>

                  {/* Customer Phone + Fraud Badge */}
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <label style={{ fontWeight: '600', color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                        Phone Number *
                      </label>
                      {customerHistoryLoading ? (
                        <span style={{ fontSize: '0.68rem', color: '#64748b' }}>Checking fraud...</span>
                      ) : customerHistory?.fraud_check ? (() => {
                        const sf = customerHistory.fraud_check.steadfast || {};
                        const tot = sf.total || 0;
                        const del = sf.delivery || 0;
                        const sRate = tot > 0 ? Math.round((del / tot) * 100) : null;
                        const isRisk = tot >= 3 && sRate !== null && sRate < 60;
                        const isGood = tot >= 2 && sRate !== null && sRate >= 80;
                        return (
                          <span
                            onClick={() => setManualModalTab('fraud')}
                            style={{
                              cursor: 'pointer',
                              fontSize: '0.68rem',
                              fontWeight: '700',
                              padding: '2px 8px',
                              borderRadius: '10px',
                              background: isRisk ? '#fef2f2' : isGood ? '#f0fdf4' : '#eff6ff',
                              color: isRisk ? '#b91c1c' : isGood ? '#15803d' : '#2563eb',
                              border: `1px solid ${isRisk ? '#fca5a5' : isGood ? '#86efac' : '#bfdbfe'}`,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '3px'
                            }}
                            title="Click to view full fraud intelligence"
                          >
                            {isRisk ? '⚠️ High Return Risk' : isGood ? `🟢 ${sRate}% Success` : sRate !== null ? `🔵 ${sRate}% Success` : 'ℹ️ New Phone'} (View)
                          </span>
                        );
                      })() : null}
                    </div>
                    <input
                      type="text"
                      value={manualForm.customer_phone}
                      onChange={e => handleManualPhoneChange(e.target.value)}
                      placeholder="01XXXXXXXXX"
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>

                  {/* Shipping Address */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#475569', marginBottom: '4px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      Shipping Address *
                    </label>
                    <textarea
                      value={manualForm.shipping_address}
                      onChange={e => setManualForm(p => ({ ...p, shipping_address: e.target.value }))}
                      placeholder="Full delivery address, area, city"
                      rows={2}
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                    />
                  </div>

                  {/* Order Notes */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#475569', marginBottom: '4px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      Order Notes (Optional)
                    </label>
                    <input
                      type="text"
                      value={manualForm.notes}
                      onChange={e => setManualForm(p => ({ ...p, notes: e.target.value }))}
                      placeholder="Any customer preferences or instructions"
                      style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>

                  {/* Delivery Option & Shipping Fee */}
                  <div style={{ marginBottom: '12px', padding: '10px 12px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <p style={{ margin: '0 0 10px', fontSize: '0.72rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', display: 'inline-block' }}>
                      🚚 Delivery Option &amp; Shipping Fee
                    </p>

                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: '#64748b', marginBottom: '4px' }}>
                        Shipping Option
                      </label>
                      <select
                        value={manualForm.delivery_option || (deliveryOptions[0]?.id || '')}
                        onChange={e => {
                          const optId = e.target.value ? parseInt(e.target.value) : null;
                          handleManualDeliveryOptionChange(optId);
                        }}
                        style={{
                          width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1',
                          borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', fontWeight: '600',
                          backgroundColor: '#f8fafc', cursor: 'pointer', outline: 'none', boxSizing: 'border-box'
                        }}
                      >
                        {deliveryOptions.map(opt => (
                          <option key={opt.id} value={opt.id}>
                            {opt.icon || '🚚'} {opt.name_en} — ৳{parseFloat(opt.cost).toFixed(0)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', color: '#64748b', marginBottom: '3px' }}>
                        Shipping Cost (৳)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={manualForm.shipping_cost}
                        onChange={e => setManualForm(p => ({ ...p, shipping_cost: e.target.value }))}
                        style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', fontWeight: '700', outline: 'none', boxSizing: 'border-box' }}
                      />
                    </div>
                  </div>

                  {/* Admin Discount */}
                  <div style={{ marginBottom: '12px', padding: '10px 12px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <p style={{ margin: '0 0 10px', fontSize: '0.72rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '2px solid #3b82f6', paddingBottom: '6px', display: 'inline-block' }}>
                      🏷️ Admin Discount
                    </p>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                      <button
                        type="button"
                        onClick={() => setManualForm(p => ({ ...p, admin_discount_type: p.admin_discount_type === 'flat' ? null : 'flat' }))}
                        style={{
                          flex: 1, padding: '6px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700',
                          border: manualForm.admin_discount_type === 'flat' ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                          background: manualForm.admin_discount_type === 'flat' ? '#eff6ff' : '#f8fafc',
                          color: manualForm.admin_discount_type === 'flat' ? '#1d4ed8' : '#64748b',
                          cursor: 'pointer'
                        }}
                      >
                        ৳ Flat
                      </button>
                      <button
                        type="button"
                        onClick={() => setManualForm(p => ({ ...p, admin_discount_type: p.admin_discount_type === 'percentage' ? null : 'percentage' }))}
                        style={{
                          flex: 1, padding: '6px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700',
                          border: manualForm.admin_discount_type === 'percentage' ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                          background: manualForm.admin_discount_type === 'percentage' ? '#eff6ff' : '#f8fafc',
                          color: manualForm.admin_discount_type === 'percentage' ? '#1d4ed8' : '#64748b',
                          cursor: 'pointer'
                        }}
                      >
                        % Percentage
                      </button>
                    </div>
                    {manualForm.admin_discount_type && (
                      <div>
                        <input
                          type="number"
                          min="0"
                          placeholder={manualForm.admin_discount_type === 'flat' ? 'Discount in ৳' : 'Discount % (e.g. 10)'}
                          value={manualForm.admin_discount_value}
                          onChange={e => setManualForm(p => ({ ...p, admin_discount_value: e.target.value }))}
                          style={{ width: '100%', padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: '7px', fontSize: '0.85rem', color: '#1e293b', fontWeight: '700', outline: 'none', boxSizing: 'border-box' }}
                        />
                        {manualAdminDiscount > 0 && (
                          <div style={{ fontSize: '0.72rem', color: '#16a34a', marginTop: '4px', fontWeight: '600' }}>
                            ✓ −৳{manualAdminDiscount.toFixed(2)} applied
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Coupon Code */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#475569', marginBottom: '4px', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      Coupon Code (Optional)
                    </label>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <input
                        value={manualForm.coupon_code}
                        onChange={e => {
                          setManualForm(p => ({ ...p, coupon_code: e.target.value }));
                          setManualCouponApplied(null);
                          setManualCouponError('');
                        }}
                        placeholder="Enter coupon"
                        style={{ flex: 1, padding: '7px 10px', borderRadius: '7px', border: `1px solid ${manualCouponApplied ? '#86efac' : manualCouponError ? '#fca5a5' : '#cbd5e1'}`, fontSize: '0.82rem', outline: 'none' }}
                      />
                      <button
                        type="button"
                        onClick={handleManualApplyCoupon}
                        disabled={manualCouponChecking || !manualForm.coupon_code.trim()}
                        style={{ padding: '7px 12px', borderRadius: '7px', background: '#475569', color: '#fff', border: 'none', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer' }}
                      >
                        {manualCouponChecking ? '...' : 'Apply'}
                      </button>
                    </div>
                    {manualCouponApplied && <div style={{ fontSize: '0.72rem', color: '#16a34a', marginTop: '3px' }}>✅ Coupon applied: −৳{manualCouponApplied.discount.toFixed(2)}</div>}
                    {manualCouponError && <div style={{ fontSize: '0.72rem', color: '#dc2626', marginTop: '3px' }}>⚠️ {manualCouponError}</div>}
                  </div>

                  {/* Send Confirmation Email */}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.78rem', color: '#374151', marginBottom: '14px' }}>
                    <input
                      type="checkbox"
                      checked={manualForm.send_email}
                      onChange={e => setManualForm(p => ({ ...p, send_email: e.target.checked }))}
                      style={{ width: '15px', height: '15px' }}
                    />
                    Send confirmation email to customer
                  </label>
                </div>
              )}

              {/* ── RIGHT: Products & Items, Packing Slip, & Fraud ── */}
              {(!isMobile || manualModalTab !== 'form') && (
                <div style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  overflowY: 'auto',
                  minWidth: 0,
                  backgroundColor: '#ffffff'
                }}>
                  {/* Right Header: Tabs & Print Button */}
                  <div style={{
                    padding: '12px 20px',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '10px',
                    background: '#ffffff',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10
                  }}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        type="button"
                        onClick={() => setManualModalTab('items')}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          padding: '7px 14px', borderRadius: '7px', fontSize: '0.82rem',
                          border: manualModalTab === 'items' ? '1px solid #bfdbfe' : '1px solid transparent',
                          background: manualModalTab === 'items' ? '#eff6ff' : '#f8fafc',
                          color: manualModalTab === 'items' ? '#1d4ed8' : '#64748b',
                          fontWeight: manualModalTab === 'items' ? '700' : '600',
                          cursor: 'pointer'
                        }}
                      >
                        🛒 Products &amp; Items ({manualCartItems.length})
                      </button>

                      <button
                        type="button"
                        onClick={() => setManualModalTab('slip')}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          padding: '7px 14px', borderRadius: '7px', fontSize: '0.82rem',
                          border: manualModalTab === 'slip' ? '1px solid #bfdbfe' : '1px solid transparent',
                          background: manualModalTab === 'slip' ? '#eff6ff' : '#f8fafc',
                          color: manualModalTab === 'slip' ? '#1d4ed8' : '#64748b',
                          fontWeight: manualModalTab === 'slip' ? '700' : '600',
                          cursor: 'pointer'
                        }}
                      >
                        🧾 Steadfast Packing Slip
                      </button>

                      <button
                        type="button"
                        onClick={() => setManualModalTab('fraud')}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          padding: '7px 14px', borderRadius: '7px', fontSize: '0.82rem',
                          border: manualModalTab === 'fraud' ? '1px solid #bfdbfe' : '1px solid transparent',
                          background: manualModalTab === 'fraud' ? '#eff6ff' : '#f8fafc',
                          color: manualModalTab === 'fraud' ? '#1d4ed8' : '#64748b',
                          fontWeight: manualModalTab === 'fraud' ? '700' : '600',
                          cursor: 'pointer'
                        }}
                      >
                        🛡️ Customer History &amp; Fraud
                      </button>
                    </div>

                    {manualModalTab === 'slip' && (
                      <button
                        type="button"
                        onClick={() => handlePrintSlip()}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '6px',
                          padding: '7px 14px', backgroundColor: '#0f172a', color: 'white',
                          border: 'none', borderRadius: '7px', fontSize: '12px',
                          fontWeight: '600', cursor: 'pointer'
                        }}
                      >
                        🖨️ Print Slip
                      </button>
                    )}
                  </div>

                  <div style={{ padding: '18px 20px', flex: 1 }}>
                    {/* ── TAB 1: Products & Items ── */}
                    {manualModalTab === 'items' && (
                      <div className="om-edit-items-container">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <div>
                            <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#1e293b' }}>
                              Order Items &amp; Variants
                            </h4>
                            <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#64748b' }}>
                              Search catalog, pick variants, adjust quantities, or customize unit prices
                            </p>
                          </div>
                          <div style={{ fontSize: '0.78rem', fontWeight: '700', color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '20px', padding: '3px 10px' }}>
                            {manualCartItems.length} {manualCartItems.length === 1 ? 'Item' : 'Items'}
                          </div>
                        </div>

                        {/* Items List */}
                        {manualCartItems.length === 0 ? (
                          <div style={{ padding: '36px', textAlign: 'center', background: '#f8fafc', borderRadius: '10px', border: '1px dashed #cbd5e1', color: '#64748b', marginBottom: '16px' }}>
                            <div style={{ fontSize: '1.8rem', marginBottom: '6px' }}>📦</div>
                            <p style={{ fontSize: '0.9rem', fontWeight: '700', color: '#1e293b', margin: '0 0 4px' }}>No products added yet</p>
                            <p style={{ fontSize: '0.78rem', color: '#64748b', margin: 0 }}>Use the search box below to find and add products to this order.</p>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
                            {manualCartItems.map((item, idx) => {
                              const attrs = item.product_attributes || {};
                              const colorOpts: any[] = attrs.colors?.options || [];
                              const sizeOpts: any[] = attrs.sizes?.options || [];
                              const weightOpts: any[] = attrs.weights?.options || [];
                              const stock = item.product_stock !== undefined ? item.product_stock : 99;
                              const subtotal = (parseFloat(item.price || 0) * parseInt(item.quantity || item.qty || 1));

                              return (
                                <div key={`manual-item-${idx}`} className="om-item-card">
                                  <div className="om-item-top">
                                    <div className="om-item-info">
                                      {item.product_image ? (
                                        <img src={item.product_image} alt="" className="om-item-thumb" />
                                      ) : (
                                        <div className="om-item-thumb" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                                          📦
                                        </div>
                                      )}
                                      <div style={{ minWidth: 0, flex: 1 }}>
                                        <div className="om-item-name" title={item.product_name}>
                                          {item.product_name}
                                        </div>
                                        <div className="om-item-meta">
                                          {item.product_sku && (
                                            <span className="om-item-sku">SKU: {item.product_sku}</span>
                                          )}
                                          <span className={`om-item-stock ${stock > 5 ? 'in-stock' : stock > 0 ? 'low-stock' : 'out-stock'}`}>
                                            ● {stock > 0 ? `${stock} in stock` : 'Out of stock'}
                                          </span>
                                        </div>
                                      </div>
                                    </div>

                                    <button
                                      type="button"
                                      className="om-item-delete-btn"
                                      onClick={() => handleRemoveManualItem(idx)}
                                      title="Remove product"
                                    >
                                      🗑️
                                    </button>
                                  </div>

                                  {/* Variant Selectors Row */}
                                  <div className="om-item-variants-row">
                                    <div className="om-variant-field">
                                      <span className="om-variant-label">Color:</span>
                                      {colorOpts.length > 0 ? (
                                        <select
                                          className="om-variant-select"
                                          value={item.selected_color || ''}
                                          onChange={(e) => handleUpdateManualItemVariant(idx, 'selected_color', e.target.value)}
                                        >
                                          <option value="">— None —</option>
                                          {colorOpts.map((c: any, cIdx: number) => {
                                            const name = typeof c === 'string' ? c : c.name;
                                            return <option key={cIdx} value={name}>{name}</option>;
                                          })}
                                        </select>
                                      ) : (
                                        <input
                                          type="text"
                                          className="om-variant-input"
                                          placeholder="e.g. Red"
                                          value={item.selected_color || ''}
                                          onChange={(e) => handleUpdateManualItemVariant(idx, 'selected_color', e.target.value)}
                                        />
                                      )}
                                    </div>

                                    <div className="om-variant-field">
                                      <span className="om-variant-label">Size:</span>
                                      {sizeOpts.length > 0 ? (
                                        <select
                                          className="om-variant-select"
                                          value={item.selected_size || ''}
                                          onChange={(e) => handleUpdateManualItemVariant(idx, 'selected_size', e.target.value)}
                                        >
                                          <option value="">— None —</option>
                                          {sizeOpts.map((s: any, sIdx: number) => {
                                            const name = typeof s === 'string' ? s : s.name;
                                            return <option key={sIdx} value={name}>{name}</option>;
                                          })}
                                        </select>
                                      ) : (
                                        <input
                                          type="text"
                                          className="om-variant-input"
                                          placeholder="e.g. XL, 42"
                                          value={item.selected_size || ''}
                                          onChange={(e) => handleUpdateManualItemVariant(idx, 'selected_size', e.target.value)}
                                        />
                                      )}
                                    </div>

                                    <div className="om-variant-field">
                                      <span className="om-variant-label">Weight:</span>
                                      {weightOpts.length > 0 ? (
                                        <select
                                          className="om-variant-select"
                                          value={item.selected_weight || ''}
                                          onChange={(e) => handleUpdateManualItemVariant(idx, 'selected_weight', e.target.value)}
                                        >
                                          <option value="">— None —</option>
                                          {weightOpts.map((w: any, wIdx: number) => {
                                            const name = typeof w === 'string' ? w : w.name;
                                            return <option key={wIdx} value={name}>{name}</option>;
                                          })}
                                        </select>
                                      ) : (
                                        <input
                                          type="text"
                                          className="om-variant-input"
                                          placeholder="e.g. 500g, 1kg"
                                          value={item.selected_weight || ''}
                                          onChange={(e) => handleUpdateManualItemVariant(idx, 'selected_weight', e.target.value)}
                                        />
                                      )}
                                    </div>
                                  </div>

                                  {/* Controls: Qty, Unit Price, Subtotal */}
                                  <div className="om-item-controls-row">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <span style={{ fontSize: '0.78rem', fontWeight: '600', color: '#64748b' }}>Quantity:</span>
                                      <div className="om-qty-stepper">
                                        <button
                                          type="button"
                                          className="om-qty-btn"
                                          onClick={() => handleUpdateManualItemQty(idx, Math.max(1, (item.quantity || 1) - 1))}
                                        >
                                          −
                                        </button>
                                        <input
                                          type="number"
                                          min="1"
                                          className="om-qty-input"
                                          value={item.quantity || 1}
                                          onChange={(e) => handleUpdateManualItemQty(idx, parseInt(e.target.value) || 1)}
                                        />
                                        <button
                                          type="button"
                                          className="om-qty-btn"
                                          onClick={() => handleUpdateManualItemQty(idx, (item.quantity || 1) + 1)}
                                        >
                                          +
                                        </button>
                                      </div>
                                    </div>

                                    <div className="om-price-field">
                                      <span>Unit Price: ৳</span>
                                      <input
                                        type="number"
                                        min="0"
                                        step="1"
                                        className="om-price-input"
                                        value={item.price}
                                        onChange={(e) => handleUpdateManualItemPrice(idx, parseFloat(e.target.value) || 0)}
                                      />
                                    </div>

                                    <div style={{ textAlign: 'right' }}>
                                      <span style={{ fontSize: '0.72rem', color: '#64748b', display: 'block' }}>Subtotal</span>
                                      <span className="om-item-subtotal-badge">৳{subtotal.toFixed(2)}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Add Product Box */}
                        <div className="om-add-product-box">
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              ➕ Add Product from Catalog
                            </span>
                            {manualProductSearching && (
                              <span style={{ fontSize: '0.75rem', color: '#2563eb', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Loader2 size={13} className="animate-spin" /> Searching catalog...
                              </span>
                            )}
                          </div>

                          <div style={{ position: 'relative' }}>
                            <div style={{ display: 'flex', alignItems: 'center', background: 'white', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '0 10px' }}>
                              <Search size={15} style={{ color: '#94a3b8', marginRight: '6px' }} />
                              <input
                                type="text"
                                placeholder="Type product name or SKU to search catalog..."
                                value={manualProductSearch}
                                onChange={(e) => handleManualProductSearch(e.target.value)}
                                style={{ width: '100%', padding: '8px 0', border: 'none', outline: 'none', fontSize: '0.84rem', color: '#1e293b' }}
                              />
                              {manualProductSearch && (
                                <button
                                  type="button"
                                  onClick={() => { setManualProductSearch(''); setManualProductResults([]); }}
                                  style={{ border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '13px' }}
                                >
                                  ✕
                                </button>
                              )}
                            </div>

                            {/* Search Dropdown Results */}
                            {manualProductResults.length > 0 && !manualPickingProduct && (
                              <div className="om-search-results-dropdown">
                                {manualProductResults.map((p) => {
                                  const price = parseFloat(p.discount_price || p.price || 0);
                                  const stock = p.stock !== undefined ? p.stock : 99;
                                  const pImg = p.image || p.images?.[0]?.image || '';
                                  const attrs = p.attributes || {};
                                  const colorOpts: any[] = (attrs.colors?.enabled && attrs.colors?.options?.length) ? attrs.colors.options : [];
                                  const sizeOpts: any[] = (attrs.sizes?.enabled && attrs.sizes?.options?.length) ? attrs.sizes.options : [];
                                  const weightOpts: any[] = (attrs.weights?.enabled && attrs.weights?.options?.length) ? attrs.weights.options : [];

                                  return (
                                    <div
                                      key={p.id}
                                      className="om-search-result-item"
                                      onClick={() => handleSelectManualProduct(p)}
                                    >
                                      {pImg ? (
                                        <img
                                          src={pImg}
                                          alt=""
                                          className="om-search-thumb"
                                          style={{
                                            width: '44px',
                                            height: '44px',
                                            minWidth: '44px',
                                            minHeight: '44px',
                                            maxWidth: '44px',
                                            maxHeight: '44px',
                                            objectFit: 'cover',
                                            borderRadius: '6px',
                                            border: '1px solid #e2e8f0',
                                            flexShrink: 0
                                          }}
                                        />
                                      ) : (
                                        <div
                                          className="om-search-thumb-placeholder"
                                          style={{
                                            width: '44px',
                                            height: '44px',
                                            minWidth: '44px',
                                            borderRadius: '6px',
                                            background: '#f1f5f9',
                                            border: '1px solid #e2e8f0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '18px',
                                            flexShrink: 0
                                          }}
                                        >
                                          📦
                                        </div>
                                      )}

                                      <div className="om-search-info">
                                        <div className="om-search-name" title={p.name_en || p.name_bn}>
                                          {p.name_en || p.name_bn}
                                        </div>
                                        <div className="om-search-sub">
                                          {p.sku && <span>SKU: {p.sku}</span>}
                                          <span style={{ color: stock > 0 ? '#16a34a' : '#dc2626', fontWeight: '600' }}>
                                            ● {stock > 0 ? `${stock} in stock` : 'Out of stock'}
                                          </span>
                                        </div>

                                        {/* Available Variants Row */}
                                        {(colorOpts.length > 0 || sizeOpts.length > 0 || weightOpts.length > 0) && (
                                          <div className="om-search-variants-pills">
                                            {colorOpts.length > 0 && (
                                              <span className="om-search-variant-pill" title={colorOpts.map((c: any) => typeof c === 'string' ? c : c.name).join(', ')}>
                                                🎨 {colorOpts.map((c: any) => typeof c === 'string' ? c : c.name).slice(0, 3).join(', ')}{colorOpts.length > 3 ? ` +${colorOpts.length - 3}` : ''}
                                              </span>
                                            )}
                                            {sizeOpts.length > 0 && (
                                              <span className="om-search-variant-pill" title={sizeOpts.map((s: any) => typeof s === 'string' ? s : s.name).join(', ')}>
                                                📏 {sizeOpts.map((s: any) => typeof s === 'string' ? s : s.name).slice(0, 3).join(', ')}{sizeOpts.length > 3 ? ` +${sizeOpts.length - 3}` : ''}
                                              </span>
                                            )}
                                            {weightOpts.length > 0 && (
                                              <span className="om-search-variant-pill" title={weightOpts.map((w: any) => typeof w === 'string' ? w : w.name).join(', ')}>
                                                ⚖️ {weightOpts.map((w: any) => typeof w === 'string' ? w : w.name).slice(0, 2).join(', ')}
                                              </span>
                                            )}
                                          </div>
                                        )}
                                      </div>

                                      <div className="om-search-price-col">
                                        <div className="om-search-price">৳{price.toFixed(2)}</div>
                                        <span className="om-search-select-btn">+ Select</span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {/* Selected Product Variant Picker */}
                          {manualPickingProduct && (
                            <div className="om-product-pick-card">
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                  {manualPickingProduct.image || manualPickingProduct.images?.[0]?.image ? (
                                    <img src={manualPickingProduct.image || manualPickingProduct.images?.[0]?.image} alt="" style={{ width: '40px', height: '40px', borderRadius: '6px', objectFit: 'cover' }} />
                                  ) : (
                                    <div style={{ width: '40px', height: '40px', borderRadius: '6px', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📦</div>
                                  )}
                                  <div>
                                    <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#1e293b' }}>{manualPickingProduct.name_en}</div>
                                    <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                                      Catalog Base: ৳{parseFloat(manualPickingProduct.discount_price || manualPickingProduct.price).toFixed(2)}
                                    </div>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setManualPickingProduct(null)}
                                  style={{ border: 'none', background: '#f1f5f9', borderRadius: '6px', padding: '4px 8px', fontSize: '11px', color: '#64748b', cursor: 'pointer' }}
                                >
                                  ✕ Cancel
                                </button>
                              </div>

                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '8px', marginBottom: '12px' }}>
                                {/* Color */}
                                {manualPickingProduct.attributes?.colors?.enabled && (
                                  <div>
                                    <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '2px' }}>Color</label>
                                    <select
                                      value={manualPickAttrs.color}
                                      onChange={(e) => setManualPickAttrs(p => ({ ...p, color: e.target.value }))}
                                      style={{ width: '100%', padding: '5px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.8rem' }}
                                    >
                                      <option value="">— None —</option>
                                      {(manualPickingProduct.attributes.colors.options || []).map((c: any, cIdx: number) => {
                                        const name = typeof c === 'string' ? c : c.name;
                                        return <option key={cIdx} value={name}>{name}</option>;
                                      })}
                                    </select>
                                  </div>
                                )}

                                {/* Size */}
                                {manualPickingProduct.attributes?.sizes?.enabled && (
                                  <div>
                                    <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '2px' }}>Size</label>
                                    <select
                                      value={manualPickAttrs.size}
                                      onChange={(e) => setManualPickAttrs(p => ({ ...p, size: e.target.value }))}
                                      style={{ width: '100%', padding: '5px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.8rem' }}
                                    >
                                      <option value="">— None —</option>
                                      {(manualPickingProduct.attributes.sizes.options || []).map((s: any, sIdx: number) => {
                                        const name = typeof s === 'string' ? s : s.name;
                                        return <option key={sIdx} value={name}>{name}</option>;
                                      })}
                                    </select>
                                  </div>
                                )}

                                {/* Weight */}
                                {manualPickingProduct.attributes?.weights?.enabled && (
                                  <div>
                                    <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '2px' }}>Weight</label>
                                    <select
                                      value={manualPickAttrs.weight}
                                      onChange={(e) => setManualPickAttrs(p => ({ ...p, weight: e.target.value }))}
                                      style={{ width: '100%', padding: '5px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.8rem' }}
                                    >
                                      <option value="">— None —</option>
                                      {(manualPickingProduct.attributes.weights.options || []).map((w: any, wIdx: number) => {
                                        const name = typeof w === 'string' ? w : w.name;
                                        return <option key={wIdx} value={name}>{name}</option>;
                                      })}
                                    </select>
                                  </div>
                                )}

                                {/* Quantity */}
                                <div>
                                  <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '2px' }}>Quantity</label>
                                  <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #cbd5e1', borderRadius: '6px', width: 'fit-content' }}>
                                    <button
                                      type="button"
                                      onClick={() => setManualPickAttrs(p => ({ ...p, qty: Math.max(1, p.qty - 1) }))}
                                      style={{ width: '26px', height: '26px', border: 'none', background: '#f8fafc', cursor: 'pointer', fontWeight: '700' }}
                                    >
                                      −
                                    </button>
                                    <span style={{ minWidth: '28px', textAlign: 'center', fontSize: '0.85rem', fontWeight: '700' }}>{manualPickAttrs.qty}</span>
                                    <button
                                      type="button"
                                      onClick={() => setManualPickAttrs(p => ({ ...p, qty: p.qty + 1 }))}
                                      style={{ width: '26px', height: '26px', border: 'none', background: '#f8fafc', cursor: 'pointer', fontWeight: '700' }}
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>

                                {/* Custom Unit Price */}
                                <div>
                                  <label style={{ fontSize: '0.7rem', fontWeight: '600', color: '#64748b', display: 'block', marginBottom: '2px' }}>Unit Price (৳)</label>
                                  <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={manualPickAttrs.price}
                                    onChange={(e) => setManualPickAttrs(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))}
                                    style={{ width: '100%', padding: '5px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', fontWeight: '700' }}
                                  />
                                </div>
                              </div>

                              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                <button
                                  type="button"
                                  onClick={handleManualAddToCart}
                                  style={{
                                    padding: '8px 18px',
                                    borderRadius: '7px',
                                    background: 'linear-gradient(135deg, #16a34a, #15803d)',
                                    color: 'white',
                                    border: 'none',
                                    fontSize: '0.82rem',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    boxShadow: '0 2px 8px rgba(22, 163, 74, 0.3)'
                                  }}
                                >
                                  ✓ Add to Order
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* ── Compact Dynamic Invoice Breakdown ── */}
                        {(() => {
                          const manualTotalUnits = manualCartItems.reduce((acc, it) => acc + (parseInt(it.quantity || it.qty || 1) || 1), 0);
                          const currentDelOpt = deliveryOptions.find(o => String(o.id) === String(manualForm.delivery_option));

                          return (
                            <div className="om-invoice-summary-card">
                              <div className="om-invoice-header">
                                <div className="om-invoice-title">
                                  🧾 Order Invoice Breakdown
                                </div>
                                <span className="om-invoice-badge-pill">
                                  COD Balance Due
                                </span>
                              </div>

                              <div className="om-invoice-body">
                                {/* Items Subtotal */}
                                <div className="om-invoice-row">
                                  <span className="om-invoice-label">
                                    Items Subtotal
                                    <span className="om-invoice-sublabel">
                                      ({manualCartItems.length} {manualCartItems.length === 1 ? 'product' : 'products'} · {manualTotalUnits} {manualTotalUnits === 1 ? 'unit' : 'units'})
                                    </span>
                                  </span>
                                  <span className="om-invoice-val">
                                    ৳{manualItemsSubtotal.toFixed(2)}
                                  </span>
                                </div>

                                {/* Delivery Fee */}
                                <div className="om-invoice-row">
                                  <span className="om-invoice-label">
                                    Delivery Fee
                                    {currentDelOpt && (
                                      <span className="om-invoice-tag">
                                        🚚 {currentDelOpt.name_en || currentDelOpt.name_bn || 'Selected'}
                                      </span>
                                    )}
                                  </span>
                                  <span className="om-invoice-val">
                                    +৳{manualShipping.toFixed(2)}
                                  </span>
                                </div>

                                {/* Admin Discount */}
                                {manualAdminDiscount > 0 && (
                                  <div className="om-invoice-row discount">
                                    <span className="om-invoice-label">
                                      Admin Discount
                                      <span className="om-invoice-sublabel">
                                        ({manualForm.admin_discount_type === 'percentage' ? `${manualForm.admin_discount_value}% off` : 'Flat deduction'})
                                      </span>
                                    </span>
                                    <span className="om-invoice-val">
                                      −৳{manualAdminDiscount.toFixed(2)}
                                    </span>
                                  </div>
                                )}

                                {/* Coupon Discount */}
                                {manualCouponDiscount > 0 && (
                                  <div className="om-invoice-row discount">
                                    <span className="om-invoice-label">
                                      Coupon Discount
                                      {manualForm.coupon_code && (
                                        <span className="om-invoice-tag coupon">
                                          🏷️ {manualForm.coupon_code.toUpperCase()}
                                        </span>
                                      )}
                                    </span>
                                    <span className="om-invoice-val">
                                      −৳{manualCouponDiscount.toFixed(2)}
                                    </span>
                                  </div>
                                )}

                                {/* Total COD Payable */}
                                <div className="om-invoice-total-row">
                                  <div className="om-invoice-total-left">
                                    <span className="om-invoice-total-title">Total (COD Amount)</span>
                                    <span className="om-invoice-total-desc">Payable by customer upon delivery</span>
                                  </div>
                                  <div className="om-invoice-total-amount">
                                    ৳{manualTotal.toFixed(2)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    {/* ── TAB 2: Steadfast Packing Slip ── */}
                    {manualModalTab === 'slip' && (
                      <div>
                        <div style={{ marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#1e293b' }}>
                              Steadfast Packing Slip &amp; Dispatch Invoice
                            </h4>
                            <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#64748b' }}>
                              Live auto-syncing preview of the courier packing slip for this manual order
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handlePrintSlip()}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '6px',
                              padding: '7px 14px', backgroundColor: '#0f172a', color: 'white',
                              border: 'none', borderRadius: '7px', fontSize: '12px',
                              fontWeight: '600', cursor: 'pointer'
                            }}
                          >
                            🖨️ Print Packing Slip
                          </button>
                        </div>

                        {/* Printable Packing Slip Container */}
                        <div
                          ref={manualPackingSlipRef}
                          style={{
                            background: '#fff',
                            border: '1.5px solid #cbd5e1',
                            borderRadius: '10px',
                            overflow: 'hidden',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                            maxWidth: '780px',
                            margin: '0 auto',
                            fontFamily: 'Inter, sans-serif'
                          }}
                        >
                          {/* Slip Header */}
                          <div style={{
                            background: 'linear-gradient(135deg, #0f172a, #1e293b)',
                            color: '#fff',
                            padding: '16px 20px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              {siteSettings?.logo_url && (
                                <img src={siteSettings?.logo_url} alt="logo" style={{ height: '36px', objectFit: 'contain', borderRadius: '4px' }} />
                              )}
                              <div>
                                <div style={{ fontSize: '16px', fontWeight: '800', letterSpacing: '-0.02em' }}>
                                  {siteSettings?.site_name || 'Online Store'}
                                </div>
                                {siteSettings?.support_phone && (
                                  <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                                    📞 {siteSettings?.support_phone}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: '9px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                                Courier Logistics
                              </div>
                              <div style={{ fontSize: '14px', fontWeight: '800', color: '#60a5fa', marginTop: '2px' }}>
                                STEADFAST COURIER
                              </div>
                            </div>
                          </div>

                          {/* Info Row: Recipient & Order Meta */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', borderBottom: '1px solid #e2e8f0' }}>
                            <div style={{ padding: '14px 18px', borderRight: '1px solid #e2e8f0' }}>
                              <div style={{ fontSize: '9px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
                                Deliver To (Recipient)
                              </div>
                              <div style={{ fontSize: '15px', fontWeight: '800', color: '#0f172a', marginBottom: '4px' }}>
                                {manualForm.customer_name || 'Customer Name'}
                              </div>
                              <div style={{ fontSize: '12px', fontWeight: '700', color: '#2563eb', marginBottom: '4px' }}>
                                📞 {manualForm.customer_phone || 'Phone Number'}
                              </div>
                              <div style={{ fontSize: '11px', color: '#475569', lineHeight: '1.4' }}>
                                📍 {manualForm.shipping_address || 'Shipping Address'}
                              </div>
                            </div>

                            <div style={{ padding: '14px 18px', background: '#f8fafc' }}>
                              <div style={{ fontSize: '9px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
                                Order Info
                              </div>
                              <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                                <tbody>
                                  <tr>
                                    <td style={{ color: '#64748b', padding: '2px 0', fontWeight: '600' }}>Type:</td>
                                    <td style={{ fontWeight: '700', textAlign: 'right', color: '#1e293b' }}>Manual (Phone)</td>
                                  </tr>
                                  <tr>
                                    <td style={{ color: '#64748b', padding: '2px 0', fontWeight: '600' }}>Date:</td>
                                    <td style={{ fontWeight: '700', textAlign: 'right', color: '#1e293b' }}>{new Date().toLocaleDateString('en-GB')}</td>
                                  </tr>
                                  <tr>
                                    <td style={{ color: '#64748b', padding: '2px 0', fontWeight: '600' }}>Status:</td>
                                    <td style={{ fontWeight: '700', textAlign: 'right', color: '#1e293b', textTransform: 'capitalize' }}>
                                      {manualForm.initial_status}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td style={{ color: '#64748b', padding: '2px 0', fontWeight: '600' }}>Area:</td>
                                    <td style={{ fontWeight: '700', textAlign: 'right', color: '#1e293b' }}>
                                      {deliveryOptions.find(o => o.id === manualForm.delivery_option)?.name_en || 'Dhaka Metro'}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Items Table */}
                          <div style={{ padding: '0' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                              <thead>
                                <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #e2e8f0', color: '#475569', textAlign: 'left' }}>
                                  <th style={{ padding: '8px 14px', fontSize: '10px', textTransform: 'uppercase', fontWeight: '700' }}>#</th>
                                  <th style={{ padding: '8px 14px', fontSize: '10px', textTransform: 'uppercase', fontWeight: '700' }}>Item Description</th>
                                  <th style={{ padding: '8px 14px', fontSize: '10px', textTransform: 'uppercase', fontWeight: '700', textAlign: 'center' }}>Qty</th>
                                  <th style={{ padding: '8px 14px', fontSize: '10px', textTransform: 'uppercase', fontWeight: '700', textAlign: 'right' }}>Price</th>
                                  <th style={{ padding: '8px 14px', fontSize: '10px', textTransform: 'uppercase', fontWeight: '700', textAlign: 'right' }}>Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {manualCartItems.length === 0 ? (
                                  <tr>
                                    <td colSpan={5} style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>
                                      No items added to this manual order yet.
                                    </td>
                                  </tr>
                                ) : (
                                  manualCartItems.map((item, i) => {
                                    const variantParts = [item.selected_color, item.selected_size, item.selected_weight].filter(Boolean);
                                    const price = parseFloat(item.price || 0);
                                    const qty = parseInt(item.quantity || item.qty || 1);
                                    const lineTotal = price * qty;
                                    return (
                                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '8px 14px', color: '#64748b' }}>{i + 1}</td>
                                        <td style={{ padding: '8px 14px' }}>
                                          <div style={{ fontWeight: '700', color: '#1e293b' }}>{item.product_name}</div>
                                          {variantParts.length > 0 && (
                                            <div style={{ fontSize: '10px', color: '#2563eb', fontWeight: '600', marginTop: '2px' }}>
                                              {variantParts.join(' · ')}
                                            </div>
                                          )}
                                        </td>
                                        <td style={{ padding: '8px 14px', textAlign: 'center', fontWeight: '700', color: '#1e293b' }}>
                                          {qty}
                                        </td>
                                        <td style={{ padding: '8px 14px', textAlign: 'right', color: '#475569' }}>
                                          ৳{price.toFixed(2)}
                                        </td>
                                        <td style={{ padding: '8px 14px', textAlign: 'right', fontWeight: '700', color: '#0f172a' }}>
                                          ৳{lineTotal.toFixed(2)}
                                        </td>
                                      </tr>
                                    );
                                  })
                                )}
                              </tbody>
                            </table>
                          </div>

                          {/* Totals Summary */}
                          <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
                            <table style={{ width: '280px', borderCollapse: 'collapse', fontSize: '11px' }}>
                              <tbody>
                                <tr>
                                  <td style={{ padding: '6px 14px', color: '#64748b' }}>Items Subtotal:</td>
                                  <td style={{ padding: '6px 14px', textAlign: 'right', fontWeight: '600', color: '#1e293b' }}>
                                    ৳{manualItemsSubtotal.toFixed(2)}
                                  </td>
                                </tr>
                                <tr>
                                  <td style={{ padding: '6px 14px', color: '#64748b' }}>Shipping Charge:</td>
                                  <td style={{ padding: '6px 14px', textAlign: 'right', fontWeight: '600', color: '#1e293b' }}>
                                    ৳{manualShipping.toFixed(2)}
                                  </td>
                                </tr>
                                {manualAdminDiscount > 0 && (
                                  <tr>
                                    <td style={{ padding: '6px 14px', color: '#16a34a' }}>Admin Discount:</td>
                                    <td style={{ padding: '6px 14px', textAlign: 'right', fontWeight: '600', color: '#16a34a' }}>
                                      −৳{manualAdminDiscount.toFixed(2)}
                                    </td>
                                  </tr>
                                )}
                                {manualCouponDiscount > 0 && (
                                  <tr>
                                    <td style={{ padding: '6px 14px', color: '#16a34a' }}>Coupon Discount:</td>
                                    <td style={{ padding: '6px 14px', textAlign: 'right', fontWeight: '600', color: '#16a34a' }}>
                                      −৳{manualCouponDiscount.toFixed(2)}
                                    </td>
                                  </tr>
                                )}
                                <tr style={{ background: '#0f172a', color: '#fff' }}>
                                  <td style={{ padding: '10px 14px', fontWeight: '800', fontSize: '13px' }}>Total COD:</td>
                                  <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: '800', fontSize: '15px', color: '#60a5fa' }}>
                                    ৳{manualTotal.toFixed(2)}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>

                          {/* Slip Footer */}
                          <div style={{ padding: '10px 18px', background: '#f1f5f9', borderTop: '1px solid #e2e8f0', fontSize: '10px', color: '#94a3b8', textAlign: 'center' }}>
                            Thank you for shopping with us! For questions regarding your delivery, please contact our support.
                          </div>
                        </div>
                      </div>
                    )}

                    {manualModalTab === 'fraud' && (
                      <div style={{ padding: '8px 4px' }}>
                        <CustomerIntelligenceWidget
                          customerHistory={customerHistory}
                          loading={customerHistoryLoading}
                          error={customerHistoryError}
                          phone={manualForm.customer_phone}
                          onRefresh={() => {
                            const clean = (manualForm.customer_phone || '').replace(/[^0-9]/g, '');
                            if (clean) loadCustomerHistory(clean);
                          }}
                          showStoreOrdersList={false}
                        />
                      </div>
                    )}

                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

  );
};
